import { type FC, useCallback, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import { useGetAllUsersQuery } from '../../store/user/user'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { useOutsideClick } from '../../common/useOutsideClick'
import { Button } from '../../components/Button/Button'

interface AdvisorFilterProps {
  accountId: string
  selectedUserIds: string[]
  disabled?: boolean
  onSelectionChange: (ids: string[]) => void
}

export const AdvisorFilter: FC<AdvisorFilterProps> = ({ accountId, selectedUserIds, disabled, onSelectionChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [pendingSelection, setPendingSelection] = useState<string[]>(selectedUserIds)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasSelectedItems = pendingSelection.length > 0

  const closeDropdown = useCallback(
    (selection: string[]) => {
      setIsOpen(false)
      setSearchTerm('')
      const prev = new Set(selectedUserIds)
      if (selection.length !== prev.size || selection.some((id) => !prev.has(id))) {
        onSelectionChange(selection)
      }
    },
    [selectedUserIds, onSelectionChange],
  )

  useOutsideClick(containerRef, () => {
    if (!isOpen) return
    closeDropdown(pendingSelection)
  })

  const [debouncedSearchTerm] = useDebounce(searchTerm, 300)

  const { data: usersData } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] }, { refetchOnMountOrArgChange: 10 })

  const users = useMemo(
    () => (usersData?.data ?? []).map((u) => ({ id: u.id, label: getUserDisplayName(u) || u.email })).sort((a, b) => a.label.localeCompare(b.label)),
    [usersData],
  )

  const filteredUsers = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase()
    if (!term) return users
    return users.filter((u) => u.label.toLowerCase().includes(term))
  }, [users, debouncedSearchTerm])

  const handleToggle = useCallback((userId: string, checked: boolean) => {
    setPendingSelection((prev) => {
      if (!checked) return prev.filter((id) => id !== userId)
      if (prev.includes(userId)) return prev
      return [...prev, userId]
    })
  }, [])

  const handleFilterButtonClick = () => {
    if (isOpen) {
      closeDropdown(pendingSelection)
    } else {
      setPendingSelection(selectedUserIds)
      setIsOpen(true)
    }
  }

  const handleClearSelectionClick = () => {
    setPendingSelection([])
  }

  const displayLabel = useMemo(() => {
    if (selectedUserIds.length === 0) return 'All advisors'
    if (selectedUserIds.length === 1) {
      const match = users.find((u) => u.id === selectedUserIds[0])
      return match?.label ?? '1 advisor'
    }
    return `${selectedUserIds.length} advisors`
  }, [selectedUserIds, users])

  return (
    <div className='insights-advisor-filter' ref={containerRef}>
      <button
        type='button'
        className='insights-advisor-filter__trigger'
        aria-haspopup='true'
        aria-expanded={isOpen}
        aria-controls='advisor-filter-dropdown'
        onClick={handleFilterButtonClick}
        disabled={disabled}
      >
        <i className='bi bi-people' />
        <span className='insights-advisor-filter__label'>{displayLabel}</span>
        <i className={classNames('bi', isOpen ? 'bi-chevron-up' : 'bi-chevron-down')} />
      </button>
      {isOpen && (
        <div id='advisor-filter-dropdown' className='insights-advisor-filter__dropdown'>
          <div className='insights-advisor-filter__search'>
            <i className='bi bi-search' />
            <Form.Control
              type='text'
              size='sm'
              placeholder='Search advisors'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              autoFocus
            />
          </div>
          <div className='insights-advisor-filter__list'>
            {filteredUsers.map((user) => (
              <Form.Check
                key={user.id}
                type='checkbox'
                id={`advisor-filter-${user.id}`}
                label={user.label}
                checked={pendingSelection.includes(user.id)}
                onChange={(e) => handleToggle(user.id, e.target.checked)}
              />
            ))}
            {filteredUsers.length === 0 && <div className='text-muted small p-2'>No advisors found</div>}
          </div>
          {hasSelectedItems && (
            <Button variant='link' className='insights-advisor-filter__clear' onClick={handleClearSelectionClick}>
              Clear selected advisors
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
